import React from "react";
import { bettorDisplayName } from "@/lib/bettorDisplayName";

function bdRow(rows, userId, key) {
  const r = rows.find((x) => String(x.user_id) === String(userId));
  return r?.breakdown?.[key] ?? 0;
}

function maxByMember(members, leaderboardRows, key) {
  let mx = 0;
  for (const m of members) {
    const v = bdRow(leaderboardRows, m.id, key);
    if (v > mx) mx = v;
  }
  return mx;
}

function totalScoreByUserId(leaderboardRows, userId) {
  const r = leaderboardRows.find((x) => String(x.user_id) === String(userId));
  return r?.score ?? 0;
}

function maxTotalScore(members, leaderboardRows) {
  let mx = 0;
  for (const m of members) {
    const v = totalScoreByUserId(leaderboardRows, m.id);
    if (v > mx) mx = v;
  }
  return mx;
}

export function OverviewSection({ title, children, titleClassName = "" }) {
  return (
    <section className="space-y-3">
      <h3
        className={`text-center font-display text-lg sm:text-xl text-[var(--accent)] tracking-wide border-b border-[var(--line)] pb-2 ${titleClassName}`.trim()}
      >
        {title}
      </h3>
      {children}
    </section>
  );
}

/** Shared picks matrix table (Översikt + matchdag export). */
export function MatrixTable({
  rowLabel,
  members,
  rows,
  leaderboardRows,
  breakdownKey = null,
  footerRows = null,
  tableClassName = "",
}) {
  const footers =
    footerRows && footerRows.length > 0
      ? footerRows
      : breakdownKey
        ? [{ key: breakdownKey, label: "Summa" }]
        : [];

  const boldMaxForRowLabel = rowLabel === "" && rows?.some((r) => r?.[0] === "Totalt");
  const totalMx = boldMaxForRowLabel ? maxTotalScore(members, leaderboardRows) : 0;

  return (
    <div className="overflow-x-auto rounded-none border-0 bg-transparent sm:rounded-lg sm:border sm:border-[var(--line)] sm:bg-[var(--panel)]/40">
      <table className={`w-max text-sm font-mono ${tableClassName}`.trim()}>
        <thead>
          <tr className="border-b border-[var(--line)] text-[var(--text-dim)]">
            <th className="sticky left-0 z-20 bg-[var(--panel)] px-1 pr-2 sm:px-2 sm:pr-3 py-2 text-left font-normal min-w-[5.75rem] sm:min-w-[7rem] shadow-sticky">
              {rowLabel}
            </th>
            {members.map((m) => (
              <th
                key={m.id}
                className="px-1 sm:px-2 py-2 text-center font-normal min-w-[4rem] sm:min-w-[4.5rem] max-w-[9rem] truncate"
                title={m.name}
              >
                {bettorDisplayName(m.name)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((cells, idx) => {
            const label = cells?.[0];
            const isTotalRow = boldMaxForRowLabel && label === "Totalt";
            return (
              <tr
                key={idx}
                className={
                  isTotalRow
                    ? "border-t-2 border-[var(--accent)]/50 bg-[var(--panel)]/60 font-semibold text-[var(--accent)]"
                    : "border-t border-[var(--line)]/70 odd:bg-[var(--bg)]/30"
                }
              >
                <td
                  className={
                    isTotalRow
                      ? "sticky left-0 z-10 bg-[var(--panel)] px-1 sm:px-2 py-2 shadow-sticky"
                      : "sticky left-0 z-10 bg-[var(--panel)] px-1 pr-2 sm:px-2 sm:pr-3 py-1.5 font-medium text-[var(--accent-2)] min-w-[5.75rem] sm:min-w-[7rem] shadow-sticky whitespace-nowrap"
                  }
                >
                  {cells[0]}
                </td>
                {cells.slice(1).map((c, i) => (
                  <td
                    key={i}
                    className={
                      isTotalRow
                        ? "px-1 sm:px-2 py-2 text-center tabular-nums"
                        : "px-1 sm:px-2 py-1.5 text-center tabular-nums text-[var(--text-dim)]"
                    }
                  >
                    {isTotalRow && totalMx > 0 && Number(c) === totalMx ? (
                      <span className="font-extrabold text-[var(--accent)]">{c}</span>
                    ) : (
                      c
                    )}
                  </td>
                ))}
              </tr>
            );
          })}
          {footers.map((fr) => {
            const mx = maxByMember(members, leaderboardRows, fr.key);
            return (
              <tr key={fr.key} className="border-t-2 border-[var(--accent)]/50 bg-[var(--panel)]/60 font-semibold text-[var(--accent)]">
                <td className="sticky left-0 z-10 bg-[var(--panel)] px-1 sm:px-2 py-2 shadow-sticky">{fr.label}</td>
                {members.map((m) => {
                  const v = bdRow(leaderboardRows, m.id, fr.key);
                  const isMax = mx > 0 && v === mx;
                  return (
                    <td key={m.id} className="px-1 sm:px-2 py-2 text-center">
                      <span className={isMax ? "font-extrabold text-[var(--accent)]" : ""}>{v}</span>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
